import Icon from "@imminently/decisively-ui/Icon";
import { get } from "lodash";
import React, { useState } from "react";

import {
  EmptyContent,
  type GraphNodeWithChildren,
  getCategories,
  getNodeDisplayData,
  scrollable,
  useCurrentRelease,
} from "@/common";
import AttributeFullExplanation from "@/components/GraphVisualisation/components/AttributeInfo/AttributeFullExplanation";
import { AttributeValueTabs } from "@/components/GraphVisualisation/components/AttributeInfo/AttributeValues";
import RuleGraphProvider from "@/components/GraphVisualisation/providers/RuleGraphProvider";
import { Popover, PopoverContent } from "@/components/radix";
import Button from "@imminently/decisively-ui/Button";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import Tabs from "@imminently/decisively-ui/Tabs";

import { AttributeUsage } from "@/pages/models/release/DataModel/route/Attributes/DocumentUsage";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import { Breadcrumbs, Typography } from "@mui/material";
import { type GraphNode, RuleDocContentsV2, getNodeKey, getNodeResult } from "@packages/commons";
import { PopoverTrigger } from "@radix-ui/react-popover";

import AttributeValue from "@imminently/decisively-ui/AttributeValue";
import NodePath from "@imminently/decisively-ui/NodePath";
import Tree, { type TreeNodeData } from "@imminently/decisively-ui/Tree";
import type { TreeItemProps } from "@imminently/decisively-ui/Tree";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

const useOutcomeData = (session: any, onSelectNode?: (node: GraphNode) => void) => {
  // const [node, setNode] = useState<GraphNodeWithType | null>(null);
  // node history, to allow for backtracking
  const [history, setHistory] = useState<GraphNode[]>(() => []);

  // node is top of history stack
  const node = history[history.length - 1];
  // this one hard overrides all history (useful for sidebar navigation)
  const selectNode = (node: GraphNode) => {
    setHistory([node]);
    onSelectNode?.(node);
  };
  // history stack manipulation, useful for clicking within the panel
  const pushNode = (node: GraphNode) => {
    setHistory((value) => [...value, node]);
  };
  const popNode = () => {
    setHistory((value) => value.slice(0, -1));
  };
  const clearHistory = () => {
    setHistory([]);
  };
  const goTo = (index: number) => {
    setHistory((value) => value.slice(0, index + 1));
  };

  return { session, node, history, selectNode, pushNode, popNode, clearHistory, goTo };
};

type OutcomeData = ReturnType<typeof useOutcomeData>;

const OutcomeContext = React.createContext<OutcomeData>(null as unknown as OutcomeData);

const useOutcomeContext = () => {
  const context = React.useContext(OutcomeContext);
  if (!context) {
    throw new Error("useOutcomeContext must be used within a OutcomeProvider");
  }
  return context;
};

const OutcomeProvider = ({
  children,
  session,
  onSelectNode,
}: {
  children: React.ReactNode;
  session: any;
  onSelectNode?: (node: GraphNode) => void;
}) => {
  const outcomeData = useOutcomeData(session, onSelectNode);
  return <OutcomeContext.Provider value={outcomeData}>{children}</OutcomeContext.Provider>;
};

type CategoryData = {
  node: GraphNodeWithChildren;
  outcome: any;
};

const CategorySummary = ({ categories }: { categories: TreeNodeData<CategoryData>[] }) => {
  return (
    <div className="flex items-center gap-1 p-4 hidden">
      {categories.map((category) => {
        const node = category.info?.node;
        if (!node) return null;
        const { backgroundColor } = getNodeDisplayData(node);
        const value = ((): string | null => {
          if (typeof node !== "object" || node == null) {
            return null;
          }

          const value = node.input || node.derived;
          if (typeof value !== "number" && typeof value !== "string" && typeof value !== "boolean") {
            return null;
          }

          return String(value);
        })();
        const tooltipLabelJSX = (
          <div className="flex flex-col gap-1">
            <Typography variant="caption">Category: {category.label}</Typography>
            <Typography variant="caption">Attribute: {get(node, ["description"], null)}</Typography>
            <Typography variant="caption">Value: {value}</Typography>
          </div>
        );
        return (
          <Tooltip key={category.id}>
            <Tooltip.Trigger>
              <div
                className="inline-flex flex-1 h-2 rounded"
                style={{ backgroundColor }}
              />
            </Tooltip.Trigger>
            <Tooltip.Content>{tooltipLabelJSX}</Tooltip.Content>
          </Tooltip>
        );
      })}
    </div>
  );
};

const useCategoryTree = (goal: string): TreeNodeData<CategoryData>[] => {
  const graph = useParsedGraph();
  const categories = graph ? getCategories(goal, graph) : [];

  // Helper to convert string to title case
  const toTitleCase = (str: string): string => {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper to format label for identifier/categoryTitle nodes without categoryName
  const formatIdentifierLabel = (node: GraphNodeWithChildren): string => {
    if (node.categoryName) {
      return node.categoryName;
    }

    // Use description (or id as fallback)
    let label = node.description || node.id || "";

    // Remove leading "the " (case insensitive)
    if (label.toLowerCase().startsWith("the ")) {
      label = label.slice(4);
    }

    // Convert to title case
    return toTitleCase(label);
  };

  const mapCategory = (node: GraphNodeWithChildren, index = 0): TreeNodeData<CategoryData> => {
    const outcome = getNodeResult(node);

    // Determine label based on node type
    let label: string;
    if (node.identifier || node.categoryTitle) {
      // For identifier/categoryTitle nodes, use special formatting
      label = formatIdentifierLabel(node);
    } else {
      // For regular category nodes, use categoryName or description
      label = node.categoryName || node.description || node.id;
    }

    return {
      id: node.path || node.id,
      label,
      info: {
        node: { ...node } as GraphNodeWithChildren,
        outcome,
      },
      children: node.children.length > 0 ? node.children.map((child, i) => mapCategory(child, i)) : undefined,
    };
  };

  return React.useMemo(() => {
    return categories.map((cat, i) => mapCategory(cat, i));
  }, [categories]);
};

const CategoryTreeItem = (props: TreeItemProps) => {
  const { node: current, selectNode } = useOutcomeContext();
  const { node, className, ...otherProps } = props;
  const categoryData = node.data?.info as CategoryData | undefined;
  const graphNode = categoryData?.node;

  if (!graphNode) {
    return null;
  }

  // Calculate display number based on node position in tree
  const pathArray: number[] = [];
  let cursor: typeof node | null = node;
  while (cursor?.parent) {
    pathArray.unshift(cursor.childIndex);
    cursor = cursor.parent as typeof node;
  }

  // Convert 0-based indices to 1-based display numbers
  const displayNum = pathArray.map((p) => p + 1).join(".");

  // For identifier nodes without a derived or input value, set derived to the index
  const nodeForAttributeValue = React.useMemo(() => {
    if (
      (graphNode.identifier || graphNode.categoryTitle) &&
      graphNode.derived === undefined &&
      graphNode.input === undefined
    ) {
      // @ts-ignore
      const index = graphNode.index ?? graphNode.path?.split("/").pop();
      return { ...graphNode, derived: typeof index === "string" ? Number(index) : index };
    }
    return graphNode;
  }, [graphNode, pathArray]);

  const tooltipTitleJSX = (
    <div className="flex flex-col gap-1">
      <Typography variant="caption">{node.data.label ?? ""}</Typography>
      <Typography variant="caption">Attribute: {get(graphNode, ["description"], null)}</Typography>
    </div>
  );

  const nodeKey = graphNode.path || graphNode.id;
  const currentNodeKey = current ? current.path || current.id : null;

  return (
    <Tree.Item
      node={node}
      className={clsx("h-full flex items-center", className)}
      {...otherProps}
    >
      <div className="flex items-center gap-2 h-full w-full rounded cursor-pointer overflow-hidden transition-colors">
        <span
          className={`ml-2 w-8 h-8 flex-shrink-0 flex items-center justify-center font-semibold ${
            node.level < 2 ? "rounded-full border border-border bg-background" : "!w-6"
          }`}
        >
          {node.level < 2 ? displayNum : null}
        </span>
        <Tooltip>
          <Tooltip.Trigger>
            <Typography
              noWrap
              className="flex-1"
            >
              {node.data.label}
            </Typography>
          </Tooltip.Trigger>
          <Tooltip.Content>{tooltipTitleJSX}</Tooltip.Content>
        </Tooltip>
        <AttributeValue
          hideTemporal
          node={nodeForAttributeValue}
        />
        <Tree.Item.Arrow />
      </div>
    </Tree.Item>
  );
};

const OutcomePanel = () => {
  const graph = useParsedGraph();
  const { session, selectNode, node: current } = useOutcomeContext();
  const categories = useCategoryTree(session.goal);

  const goal = graph?.node(session.goal) ?? { derived: undefined, description: "No goal" };

  // Track selected ID based on current node
  const selectedId = React.useMemo(() => {
    if (!current) {
      return null;
    }
    const nodeKey = current.path || current.id;
    return nodeKey;
  }, [current]);

  const handleSelect = (id: string) => {
    // Find the node and select it
    const findNode = (nodes: TreeNodeData<CategoryData>[]): GraphNodeWithChildren | null => {
      for (const node of nodes) {
        if (node.id === id && node.info?.node) {
          return node.info.node;
        }
        if (node.children) {
          const found = findNode(node.children);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    const foundNode = findNode(categories);
    if (foundNode) {
      selectNode(foundNode);
    }
  };

  return (
    <div
      id="outcome"
      className="flex flex-col h-full"
    >
      <div
        className="flex flex-col gap-2 p-4 cursor-pointer"
        onClick={() => selectNode(goal)}
      >
        <Typography>Decision Outcome</Typography>
        <Typography variant="h4">{goal.description}</Typography>
        <div>
          <AttributeValue
            hideTemporal
            node={goal}
          />
        </div>
      </div>
      <CategorySummary categories={categories} />
      <div className="flex flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          <Typography style={{ fontWeight: 600 }}>Assessment categories</Typography>
          <Tooltip>
            <Tooltip.Trigger>
              <Icon
                name="info"
                className="text-muted-foreground"
              />
            </Tooltip.Trigger>
            <Tooltip.Content>
              Below are the key categories that make up this assessment and their associated outcomes
            </Tooltip.Content>
          </Tooltip>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <Tree
          data={categories}
          selectedId={selectedId}
          onSelect={handleSelect}
          rowHeight={46}
          openByDefault={true}
          showIndentBorders={false}
        >
          {(props) => <CategoryTreeItem {...props} />}
        </Tree>
      </div>
    </div>
  );
};

export const DecisionOutcomeListPanel = ({
  session,
  onSelectNode,
}: {
  session: any;
  onSelectNode?: (node: GraphNode) => void;
}) => {
  return (
    <OutcomeProvider
      session={session}
      onSelectNode={onSelectNode}
    >
      <OutcomePanel />
    </OutcomeProvider>
  );
};

const HistoryBreadcrumb = () => {
  const { history, popNode, goTo } = useOutcomeContext();
  const previous = history[history.length - 2];
  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      style={{ height: "1.5rem" }}
    >
      <Button
        icon
        disabled={history.length <= 1}
        onClick={popNode}
        label="Back"
      >
        <Icon name="go-back" />
      </Button>
      <Popover>
        <PopoverTrigger disabled={history.length <= 2}>
          <span>...</span>
        </PopoverTrigger>
        <PopoverContent>
          <div className="flex flex-col">
            {history.map((node, index) => (
              <Button
                key={node.id}
                variant="ghost"
                onClick={() => goTo(index)}
                label={node.description ?? ""}
              >
                {node.description}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {previous ? <span onClick={popNode}>{previous.description}</span> : null}
    </Breadcrumbs>
  );
};

const AttributeOverviewHeader = ({
  node,
  canGoBack,
  onBack,
}: {
  node: GraphNode;
  canGoBack: boolean;
  onBack: () => void;
}) => {
  const onHistoryClick = () => {
    console.log("History click");
  };

  return (
    <div className="flex flex-col gap-1 p-4 border-b border-border">
      {/* <HistoryBreadcrumb /> */}
      <div className="flex items-center gap-4">
        <Button
          icon
          disabled={!canGoBack}
          onClick={onBack}
          label="Back"
        >
          <Icon name="go-back" />
        </Button>
        <div className="flex min-w-0 flex-[3] flex-col gap-2">
          <Typography variant="h4">{node.description}</Typography>
          {node.path ? (
            <div className="flex min-w-0 items-center gap-2">
              <Typography
                variant="caption"
                className="shrink-0 text-muted-foreground"
              >
                Path:
              </Typography>
              <NodePath
                node={node}
                showDescription={false}
                className="min-w-0 flex-1"
              />
            </div>
          ) : null}
          {node.definedIn ? (
            <AttributeUsage
              node={node}
              showUsedIn={false}
              onClick={close}
            />
          ) : null}
        </div>
        <div
          className="flex min-w-[120px] flex-1 justify-end"
          onClick={onHistoryClick}
        >
          <div
            className={
              "bg-secondary border border-border py-1 px-2 flex gap-2 items-center justify-between rounded-md cursor-pointer min-w-[100px]"
            }
          >
            Outcome:
            <AttributeValue node={node} />
          </div>
        </div>
      </div>
    </div>
  );
};

const DocumentPanel = ({ node }: { node?: GraphNode }) => {
  return <AttributeFullExplanation attr={node} />;
};

const InfoPanel = () => {
  return <AttributeValueTabs />;
};

export const AttributeExplanationDetailPanel = ({
  graph,
  node,
  canGoBack = false,
  onBack = () => undefined,
  onNavigate,
}: {
  graph: any;
  node: GraphNode;
  canGoBack?: boolean;
  onBack?: () => void;
  onNavigate: (node: GraphNode) => void;
}) => {
  const isInput = node.definedIn === undefined;

  const onNodeChange = (nextNode: GraphNode) => {
    if (!nextNode) return;

    const isDifferentId = nextNode.id !== node.id;
    const isSameIdDifferentPath = nextNode.id === node.id && (nextNode.path || nextNode.id) !== (node.path || node.id);

    if (isDifferentId || isSameIdDifferentPath) {
      onNavigate(nextNode);
    }
  };

  return (
    <div
      id="overview"
      className="flex flex-col h-full overflow-auto"
    >
      <RuleGraphProvider
        key={node.path || node.id}
        onSelectedNodeChange={onNodeChange}
        startNode={node}
        debug={true}
        graph={graph}
      >
        <AttributeOverviewHeader
          node={node}
          canGoBack={canGoBack}
          onBack={onBack}
        />
        <SplitPane
          direction="horizontal"
          className="flex-1"
        >
          <SplitPane.Pane
            minSize={200}
            className="flex flex-col overflow-auto"
          >
            {isInput ? (
              <div className="flex flex-col gap-4 px-2 mt-4">
                <AttributeUsage
                  node={node}
                  showUsedIn={true}
                />
              </div>
            ) : (
              <DocumentPanel node={node} />
            )}
          </SplitPane.Pane>
          <SplitPane.Pane
            minSize={200}
            className="flex flex-col overflow-auto"
          >
            <InfoPanel />
          </SplitPane.Pane>
        </SplitPane>
      </RuleGraphProvider>
    </div>
  );
};

const OverviewPanel = () => {
  const graph = useParsedGraph();
  const { node, pushNode } = useOutcomeContext();

  const onNodeChange = (n: GraphNode) => {
    // block if its the same node
    // Compare by id first (original behavior), but also allow changes if path differs
    // This fixes the issue where nodes with same id but different paths weren't being selected,
    // while still allowing normal navigation
    if (!n) return;

    const isDifferentId = n.id !== node.id;
    const isSameIdDifferentPath = n.id === node.id && (n.path || n.id) !== (node.path || node.id);

    if (isDifferentId || isSameIdDifferentPath) {
      console.log("Node change", n);
      pushNode(n);
    }
  };

  const { popNode, history } = useOutcomeContext();

  return (
    <AttributeExplanationDetailPanel
      graph={graph}
      node={node}
      canGoBack={history.length > 1}
      onBack={popNode}
      onNavigate={onNodeChange}
    />
  );
};

export const DecisionPanel = ({ session }: { session: any }) => {
  return (
    <OutcomeProvider session={session}>
      <SplitPane
        direction="horizontal"
        className="h-full"
        gutterSize={8}
      >
        <SplitPane.Pane
          minSize={300}
          preferredSize="25%"
          className="flex flex-col"
        >
          <OutcomePanel />
        </SplitPane.Pane>
        <SplitPane.Pane
          minSize={400}
          preferredSize="75%"
          className="flex flex-col"
        >
          <OutcomeContext.Consumer>
            {({ node }) =>
              node ? (
                <OverviewPanel />
              ) : (
                <div
                  id="overview"
                  className="flex flex-col h-full overflow-auto"
                >
                  <EmptyContent messages={["Select a category"]} />
                </div>
              )
            }
          </OutcomeContext.Consumer>
        </SplitPane.Pane>
      </SplitPane>
    </OutcomeProvider>
  );
};
